/**
 * Grow's photographs: real photos from the Visma Image bank (stockpress, licensed for Visma
 * companies; never AI-generated). The files live in public/images/photos, named by the slot they
 * fill, with the source of each in public/images/photos/CREDITS.md.
 *
 * Every photo here is decorative (alt="", aria-hidden): the text beside it always says what
 * matters. Each one carries its focal point as a CSS object-position, so a crop to any box keeps
 * the faces in it.
 */

export interface Photo {
  /** 1x file. */
  src: string;
  /** 2x file, where the slot is shown large enough to need it. */
  src2x?: string;
  /** Intrinsic size of the 1x file, so the browser reserves the box before it loads. */
  width: number;
  height: number;
  /** CSS object-position: where the faces are. */
  position: string;
}

const BASE = '/images/photos';

function photo(name: string, width: number, height: number, position: string, has2x = false): Photo {
  return {
    src: `${BASE}/${name}.webp`,
    ...(has2x ? { src2x: `${BASE}/${name}@2x.webp` } : {}),
    width,
    height,
    position,
  };
}

/** A srcset for an <img>, or undefined when the photo has one size only. */
export function photoSrcSet(p: Photo): string | undefined {
  return p.src2x ? `${p.src} 1x, ${p.src2x} 2x` : undefined;
}

/** The sign-in panel: one of these per visit. */
export const SIGNIN_PHOTOS: readonly Photo[] = [
  photo('signin-1', 1600, 1067, '40% 30%'),
  photo('signin-2', 1600, 1077, '55% 30%'),
  photo('signin-3', 1600, 1107, '60% 40%'),
  photo('signin-4', 1600, 1067, '55% 40%'),
  photo('signin-5', 1600, 1067, '28% 40%'),
];

export const PHOTOS = {
  aboutHero: photo('about-hero', 1600, 1095, '70% 35%'),
  pillarLearn: photo('pillar-learn', 800, 267, '50% 60%', true),
  pillarAppreciate: photo('pillar-appreciate', 800, 267, '50% 30%', true),
  pillarGrow: photo('pillar-grow', 800, 267, '50% 50%', true),
  aboutCta: photo('about-cta', 800, 400, '55% 62%', true),
  dashboardWelcome: photo('dashboard-welcome', 800, 547, '35% 35%', true),
  emptyEvents: photo('empty-events', 800, 543, '55% 35%'),
  emptyCourses: photo('empty-courses', 800, 551, '65% 40%'),
  emptyKudos: photo('empty-kudos', 800, 528, '50% 40%'),
  offline: photo('offline', 1600, 1085, '50% 35%'),
} as const;

const COVER = {
  workshop: photo('cover-workshop', 800, 450, '50% 30%', true),
  workshop2: photo('cover-workshop-2', 800, 450, '45% 30%', true),
  workshop3: photo('cover-workshop-3', 800, 450, '55% 30%', true),
  workshop4: photo('cover-workshop-4', 800, 450, '50% 30%', true),
  workshop5: photo('cover-workshop-5', 800, 450, '50% 30%', true),
  workshop6: photo('cover-workshop-6', 800, 450, '50% 40%', true),
  workshop7: photo('cover-workshop-7', 800, 450, '50% 40%', true),
  workshop8: photo('cover-workshop-8', 800, 450, '50% 35%', true),
  talk: photo('cover-talk', 800, 450, '45% 50%', true),
  talk2: photo('cover-talk-2', 800, 450, '55% 30%', true),
  talk3: photo('cover-talk-3', 800, 450, '55% 45%', true),
  talk4: photo('cover-talk-4', 800, 450, '50% 25%', true),
  social: photo('cover-social', 800, 450, '50% 40%', true),
  social2: photo('cover-social-2', 800, 450, '55% 40%', true),
  social3: photo('cover-social-3', 800, 450, '55% 30%', true),
  social4: photo('cover-social-4', 800, 450, '55% 40%', true),
  course: photo('cover-course', 800, 450, '45% 35%', true),
  course2: photo('cover-course-2', 800, 450, '35% 35%', true),
  course3: photo('cover-course-3', 800, 450, '50% 35%', true),
  course4: photo('cover-course-4', 800, 450, '65% 45%', true),
  training: photo('cover-training', 800, 450, '60% 50%', true),
};

/** The covers a group of event types or courses may get; one is picked per item, by its id. */
const COVER_GROUPS = {
  // Most of the catalogue is workshops (a programme runs dozens), so this group has the most.
  workshop: [
    COVER.workshop, COVER.workshop2, COVER.workshop3, COVER.workshop4,
    COVER.workshop5, COVER.workshop6, COVER.workshop7, COVER.workshop8,
  ],
  talk: [COVER.talk, COVER.talk2, COVER.talk3, COVER.talk4],
  social: [COVER.social, COVER.social2, COVER.social3, COVER.social4],
  learning: [COVER.training, COVER.course, COVER.course2, COVER.course3, COVER.course4],
} as const;

type CoverGroup = keyof typeof COVER_GROUPS;

/** Event types (EventInput.EVENT_TYPES) to the kind of picture that suits them. */
const EVENT_GROUP: Record<string, CoverGroup> = {
  workshop: 'workshop',
  hackathon: 'workshop',
  'tech talk': 'talk',
  conference: 'talk',
  training: 'learning',
  learning: 'learning',
  social: 'social',
  teambuilding: 'social',
  networking: 'social',
  party: 'social',
  sports: 'social',
  wellness: 'social',
  entertainment: 'social',
};

/**
 * Default covers on or off. On (owner, 2026-11-05): a course or event without its own picture is
 * SHOWN with a Visma photo for its type. Off brings back the tinted type covers and date tiles.
 */
export const DEFAULT_COVERS = true;

/** A small, stable hash, so the same id always lands on the same photo (no change on reload). */
export function stableIndex(id: string | number | null | undefined, n: number): number {
  if (n <= 1) return 0;
  const s = String(id ?? '');
  // FNV-1a, then a final mix, so ids that differ in one character still spread across the set.
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) h = Math.imul(h ^ s.charCodeAt(i), 0x01000193);
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  return (h >>> 0) % n;
}

/**
 * The picture an event (or a workshop, which is an event of type Workshop) is SHOWN with when it
 * has none of its own. It is a render-time fallback only: nothing is stored, the editor's cover
 * field still says "No image yet", and a cover an organiser uploads always wins (pass it as
 * `own`; when set, this returns null and the caller shows that instead).
 */
export function defaultEventCover(eventType: string | null | undefined, id: string | number | null | undefined, own?: string | null): Photo | null {
  if (own || !DEFAULT_COVERS) return null;
  const group = EVENT_GROUP[String(eventType || '').trim().toLowerCase()] ?? 'social';
  const list = COVER_GROUPS[group];
  return list[stableIndex(id, list.length)];
}

/** How many cards back a grid looks when it spreads covers: the left neighbour and, in a 2 or 3
 *  column grid, the card above. */
const SPREAD_WINDOW = 3;

function spread<T>(
  items: readonly T[],
  idOf: (item: T) => string | number | null | undefined,
  listOf: (item: T) => readonly Photo[] | null,
): Map<string, Photo | null> {
  const out = new Map<string, Photo | null>();
  const recent: Photo[] = [];
  items.forEach((item) => {
    const key = String(idOf(item) ?? '');
    const list = listOf(item);
    if (!list || list.length === 0) { out.set(key, null); return; }
    const base = stableIndex(idOf(item), list.length);
    // Its own photo, unless a card next to it already shows that one: then the next free one.
    let chosen = list[base];
    for (let step = 0; step < list.length; step += 1) {
      const candidate = list[(base + step) % list.length];
      if (!recent.includes(candidate)) { chosen = candidate; break; }
    }
    out.set(key, chosen);
    recent.push(chosen);
    if (recent.length > SPREAD_WINDOW - 1) recent.shift();
  });
  return out;
}

/**
 * Default covers for a whole grid of events, in display order: each keeps its own photo (by id)
 * unless a neighbouring card already shows it, so a page of Spark workshop sessions is not a run
 * of the same picture. The same list in the same order always gives the same covers. Events with
 * their own picture map to null.
 */
export function spreadEventCovers(events: readonly any[]): Map<string, Photo | null> {
  return spread(events, (e) => e?.id, (e) => {
    if (e?.image_url || !DEFAULT_COVERS) return null;
    return COVER_GROUPS[EVENT_GROUP[String(e?.event_type || '').trim().toLowerCase()] ?? 'social'];
  });
}

/** The same for a grid of courses. `skip` leaves some out (a course with no lessons yet). */
export function spreadCourseCovers(courses: readonly any[], skip: (c: any) => boolean = () => false): Map<string, Photo | null> {
  return spread(courses, (c) => c?.id, (c) => (c?.thumbnail_url || skip(c) || !DEFAULT_COVERS ? null : COVER_GROUPS.learning));
}

/** The same for a course: a learning photo, chosen by its id. Its own thumbnail always wins. */
export function defaultCourseCover(id: string | number | null | undefined, own?: string | null): Photo | null {
  if (own || !DEFAULT_COVERS) return null;
  const list = COVER_GROUPS.learning;
  return list[stableIndex(id, list.length)];
}

let signinPick: Photo | null = null;

/**
 * The sign-in photo for this page load: picked at random once, then the same on every render
 * (so it never swaps under the person). `random` is for tests.
 */
export function signinPhoto(random: () => number = Math.random): Photo {
  if (!signinPick) {
    const i = Math.min(SIGNIN_PHOTOS.length - 1, Math.floor(random() * SIGNIN_PHOTOS.length));
    signinPick = SIGNIN_PHOTOS[Math.max(0, i)];
  }
  return signinPick;
}

/** Tests only: forget this page load's pick. */
export function resetSigninPhoto(): void {
  signinPick = null;
}
